import type { FC } from 'react';
import { Typography } from '@interstate/components/Typography';
import type { ReviewViewProps } from '../types';
import {
    ReviewContainer,
    ReviewHeader,
    WillAttachToSection,
    AttachToGrid,
    AttachToColumn,
    AttachToColumnGroup,
    AttachToValue,
    MobileOnly,
    DesktopOnly,
    ChangeSummarySection,
    ChangeSummaryColumns,
    ChangeSummaryColumn,
    DataSection,
    DataRow,
    DataRowWithBadge,
    DataContent,
    DataGroup
} from '../CreditAppSearch.styles';
import { formatPhoneNumber } from '../../utils/formatUtils';
import { isFieldUpdated, isFieldNew, formatValue } from '../utils/reviewHelpers';
import { UpdatedBadge } from '../components/UpdatedBadge';
import { NewBadge } from '../components/NewBadge';

/**
 * Review view component for credit app search
 * Displays comparison between current deal and selected credit app
 * Shows "Updated" badge for changed fields and "New" badge for new fields
 */
export const ReviewView: FC<ReviewViewProps> = ({ currentDealData, newDealData }) => {
    // Helper to determine badge for a group: Updated takes precedence over New
    const getGroupBadge = (hasUpdated: boolean, hasNew: boolean) => {
        if (hasUpdated) return <UpdatedBadge />;
        if (hasNew) return <NewBadge />;
        return null;
    };

    // Buyer Name Group
    const buyerNameHasUpdated =
        isFieldUpdated(currentDealData.buyer.firstName, newDealData.buyer.firstName) ||
        isFieldUpdated(currentDealData.buyer.middleName, newDealData.buyer.middleName) ||
        isFieldUpdated(currentDealData.buyer.lastName, newDealData.buyer.lastName);

    const buyerNameHasNew =
        isFieldNew(currentDealData.buyer.firstName, newDealData.buyer.firstName) ||
        isFieldNew(currentDealData.buyer.middleName, newDealData.buyer.middleName) ||
        isFieldNew(currentDealData.buyer.lastName, newDealData.buyer.lastName);

    // Buyer Address Group
    const buyerAddressHasUpdated =
        isFieldUpdated(currentDealData.buyer.address.street, newDealData.buyer.address.street) ||
        isFieldUpdated(currentDealData.buyer.address.city, newDealData.buyer.address.city) ||
        isFieldUpdated(currentDealData.buyer.address.state, newDealData.buyer.address.state) ||
        isFieldUpdated(currentDealData.buyer.address.zip, newDealData.buyer.address.zip);

    const buyerAddressHasNew =
        isFieldNew(currentDealData.buyer.address.street, newDealData.buyer.address.street) ||
        isFieldNew(currentDealData.buyer.address.city, newDealData.buyer.address.city) ||
        isFieldNew(currentDealData.buyer.address.state, newDealData.buyer.address.state) ||
        isFieldNew(currentDealData.buyer.address.zip, newDealData.buyer.address.zip);

    // Co-Buyer Name Group
    const coBuyerNameHasUpdated =
        currentDealData.coBuyer &&
        newDealData.coBuyer &&
        (isFieldUpdated(currentDealData.coBuyer.firstName, newDealData.coBuyer.firstName) ||
            isFieldUpdated(currentDealData.coBuyer.middleName, newDealData.coBuyer.middleName) ||
            isFieldUpdated(currentDealData.coBuyer.lastName, newDealData.coBuyer.lastName));

    const coBuyerNameHasNew = !currentDealData.coBuyer && newDealData.coBuyer;

    // Co-Buyer Address Group
    const coBuyerAddressHasUpdated =
        currentDealData.coBuyer &&
        newDealData.coBuyer &&
        (isFieldUpdated(currentDealData.coBuyer.address.street, newDealData.coBuyer.address.street) ||
            isFieldUpdated(currentDealData.coBuyer.address.city, newDealData.coBuyer.address.city) ||
            isFieldUpdated(currentDealData.coBuyer.address.state, newDealData.coBuyer.address.state) ||
            isFieldUpdated(currentDealData.coBuyer.address.zip, newDealData.coBuyer.address.zip));

    const coBuyerAddressHasNew = !currentDealData.coBuyer && newDealData.coBuyer;

    return (
        <ReviewContainer data-testid="review-view">
            <ReviewHeader>
                <Typography variant="h4">Review & Merge</Typography>
                <Typography variant="body-sm">
                    Review the final changes before applying them to the deal. Updated fields are highlighted
                </Typography>
            </ReviewHeader>

            <WillAttachToSection>
                <Typography variant="h4">Will Attach To:</Typography>
                <Typography variant="h5" data-testid="customer-name" color="sem.color.surface.button.primary.rest">
                    {`${currentDealData.buyer.firstName} ${currentDealData.buyer.lastName}`}
                </Typography>

                <AttachToGrid>
                    <AttachToColumn>
                        <MobileOnly>
                            <Typography variant="body-xs" color="base.color.gray.600">
                                Buyer
                            </Typography>
                        </MobileOnly>
                        <AttachToColumnGroup>
                            <Typography variant="body-xs" color="base.color.gray.600">
                                Address
                            </Typography>
                            <AttachToValue>
                                <Typography variant="body-sm">{formatValue(currentDealData.buyer.address.street)}</Typography>
                            </AttachToValue>
                            <AttachToValue>
                                <Typography variant="body-sm">
                                    {formatValue(currentDealData.buyer.address.city)},{' '}
                                    {formatValue(currentDealData.buyer.address.state)},{' '}
                                    {formatValue(currentDealData.buyer.address.zip)}
                                </Typography>
                            </AttachToValue>
                        </AttachToColumnGroup>

                        <MobileOnly>
                            <AttachToColumnGroup>
                                <Typography variant="body-xs" color="base.color.gray.600">
                                    Phone
                                </Typography>
                                <AttachToValue>
                                    <Typography variant="body-sm">
                                        {formatValue(formatPhoneNumber(currentDealData.buyer.phoneNumber) || '')}
                                    </Typography>
                                </AttachToValue>
                            </AttachToColumnGroup>

                            <AttachToColumnGroup>
                                <Typography variant="body-xs" color="base.color.gray.600">
                                    Email Address
                                </Typography>
                                <AttachToValue>
                                    <Typography variant="body-sm">{formatValue(currentDealData.buyer.emailAddress)}</Typography>
                                </AttachToValue>
                            </AttachToColumnGroup>
                        </MobileOnly>
                    </AttachToColumn>
                    <DesktopOnly as={AttachToColumn}>
                        <Typography variant="body-xs" color="base.color.gray.600">
                            Phone
                        </Typography>
                        <AttachToValue>
                            <Typography variant="body-sm">
                                {formatValue(formatPhoneNumber(currentDealData.buyer.phoneNumber) || '')}
                            </Typography>
                        </AttachToValue>
                    </DesktopOnly>
                    <DesktopOnly as={AttachToColumn}>
                        <Typography variant="body-xs" color="base.color.gray.600">
                            Email Address
                        </Typography>
                        <AttachToValue>
                            <Typography variant="body-sm">{formatValue(currentDealData.buyer.emailAddress)}</Typography>
                        </AttachToValue>
                    </DesktopOnly>
                    <AttachToColumn>
                        <Typography variant="body-xs" color="base.color.gray.600">
                            Co-Buyer
                        </Typography>
                        {currentDealData.coBuyer ? (
                            <>
                                <MobileOnly>
                                    <AttachToColumnGroup>
                                        <Typography variant="body-xs" color="base.color.gray.600">
                                            Address
                                        </Typography>
                                        <AttachToValue>
                                            <Typography variant="body-sm">
                                                {formatValue(currentDealData.coBuyer.address.street)}
                                            </Typography>
                                        </AttachToValue>
                                        <AttachToValue>
                                            <Typography variant="body-sm">
                                                {formatValue(currentDealData.coBuyer.address.city)},{' '}
                                                {formatValue(currentDealData.coBuyer.address.state)},{' '}
                                                {formatValue(currentDealData.coBuyer.address.zip)}
                                            </Typography>
                                        </AttachToValue>
                                    </AttachToColumnGroup>

                                    <AttachToColumnGroup>
                                        <Typography variant="body-xs" color="base.color.gray.600">
                                            Phone
                                        </Typography>
                                        <AttachToValue>
                                            <Typography variant="body-sm">
                                                {formatValue(formatPhoneNumber(currentDealData.coBuyer.phoneNumber) || '')}
                                            </Typography>
                                        </AttachToValue>
                                    </AttachToColumnGroup>

                                    <AttachToColumnGroup>
                                        <Typography variant="body-xs" color="base.color.gray.600">
                                            Email Address
                                        </Typography>
                                        <AttachToValue>
                                            <Typography variant="body-sm">
                                                {formatValue(currentDealData.coBuyer.emailAddress)}
                                            </Typography>
                                        </AttachToValue>
                                    </AttachToColumnGroup>
                                </MobileOnly>

                                {/* Desktop only: Just the name */}
                                <DesktopOnly>
                                    <AttachToValue>
                                        <Typography variant="body-sm">
                                            {`${currentDealData.coBuyer.firstName} ${currentDealData.coBuyer.lastName}`}
                                        </Typography>
                                    </AttachToValue>
                                </DesktopOnly>
                            </>
                        ) : (
                            <AttachToValue>
                                <Typography variant="body-sm">--</Typography>
                            </AttachToValue>
                        )}
                    </AttachToColumn>
                </AttachToGrid>
            </WillAttachToSection>

            <ChangeSummarySection>
                <Typography variant="h4">Change Summary</Typography>

                <ChangeSummaryColumns>
                    <ChangeSummaryColumn>
                        <Typography variant="h3">Existing Deal Information</Typography>
                        <Typography variant="body-sm">The following information is currently associated with this deal:</Typography>

                        <DataSection>
                            <div>
                                <DataGroup>
                                    <Typography variant="body-xs">Buyer</Typography>
                                    <DataRow>
                                        <Typography variant="body-sm" tag="span">
                                            First Name:
                                        </Typography>
                                        <Typography variant="body-sm" tag="span">
                                            {formatValue(currentDealData.buyer.firstName)}
                                        </Typography>
                                    </DataRow>
                                    {currentDealData.buyer.middleName && (
                                        <DataRow>
                                            <Typography variant="body-sm" tag="span">
                                                Middle:
                                            </Typography>
                                            <Typography variant="body-sm" tag="span">
                                                {formatValue(currentDealData.buyer.middleName)}
                                            </Typography>
                                        </DataRow>
                                    )}
                                    <DataRow>
                                        <Typography variant="body-sm" tag="span">
                                            Last Name:
                                        </Typography>
                                        <Typography variant="body-sm" tag="span">
                                            {formatValue(currentDealData.buyer.lastName)}
                                        </Typography>
                                    </DataRow>
                                </DataGroup>

                                <DataGroup>
                                    <Typography variant="body-xs">Address</Typography>
                                    <DataRow>
                                        <Typography variant="body-sm" tag="span">
                                            Street:
                                        </Typography>
                                        <Typography variant="body-sm" tag="span">
                                            {formatValue(currentDealData.buyer.address.street)}
                                        </Typography>
                                    </DataRow>
                                    <DataRow>
                                        <Typography variant="body-sm" tag="span">
                                            City:
                                        </Typography>
                                        <Typography variant="body-sm" tag="span">
                                            {formatValue(currentDealData.buyer.address.city)}
                                        </Typography>
                                    </DataRow>
                                    <DataRow>
                                        <Typography variant="body-sm" tag="span">
                                            State:
                                        </Typography>
                                        <Typography variant="body-sm" tag="span">
                                            {formatValue(currentDealData.buyer.address.state)}
                                        </Typography>
                                    </DataRow>
                                    <DataRow>
                                        <Typography variant="body-sm" tag="span">
                                            ZIP:
                                        </Typography>
                                        <Typography variant="body-sm" tag="span">
                                            {formatValue(currentDealData.buyer.address.zip)}
                                        </Typography>
                                    </DataRow>
                                </DataGroup>
                            </div>

                            {currentDealData.coBuyer && (
                                <div>
                                    <Typography variant="body-xs">Co-Buyer</Typography>

                                    <DataGroup>
                                        <DataRow>
                                            <Typography variant="body-sm" tag="span">
                                                First Name:
                                            </Typography>
                                            <Typography variant="body-sm" tag="span">
                                                {formatValue(currentDealData.coBuyer.firstName)}
                                            </Typography>
                                        </DataRow>
                                        {currentDealData.coBuyer.middleName && (
                                            <DataRow>
                                                <Typography variant="body-sm" tag="span">
                                                    Middle:
                                                </Typography>
                                                <Typography variant="body-sm" tag="span">
                                                    {formatValue(currentDealData.coBuyer.middleName)}
                                                </Typography>
                                            </DataRow>
                                        )}
                                        <DataRow>
                                            <Typography variant="body-sm" tag="span">
                                                Last Name:
                                            </Typography>
                                            <Typography variant="body-sm" tag="span">
                                                {formatValue(currentDealData.coBuyer.lastName)}
                                            </Typography>
                                        </DataRow>
                                    </DataGroup>

                                    <DataGroup>
                                        <Typography variant="body-xs">Address</Typography>
                                        <DataRow>
                                            <Typography variant="body-sm" tag="span">
                                                Street:
                                            </Typography>
                                            <Typography variant="body-sm" tag="span">
                                                {formatValue(currentDealData.coBuyer.address.street)}
                                            </Typography>
                                        </DataRow>
                                        <DataRow>
                                            <Typography variant="body-sm" tag="span">
                                                City:
                                            </Typography>
                                            <Typography variant="body-sm" tag="span">
                                                {formatValue(currentDealData.coBuyer.address.city)}
                                            </Typography>
                                        </DataRow>
                                        <DataRow>
                                            <Typography variant="body-sm" tag="span">
                                                State:
                                            </Typography>
                                            <Typography variant="body-sm" tag="span">
                                                {formatValue(currentDealData.coBuyer.address.state)}
                                            </Typography>
                                        </DataRow>
                                        <DataRow>
                                            <Typography variant="body-sm" tag="span">
                                                ZIP:
                                            </Typography>
                                            <Typography variant="body-sm" tag="span">
                                                {formatValue(currentDealData.coBuyer.address.zip)}
                                            </Typography>
                                        </DataRow>
                                    </DataGroup>
                                </div>
                            )}
                        </DataSection>
                    </ChangeSummaryColumn>

                    <ChangeSummaryColumn>
                        <Typography variant="h3">Updated Deal Information</Typography>
                        <Typography variant="body-sm">
                            The following information from will be merged to the current deal:
                        </Typography>

                        <DataSection>
                            <div>
                                <Typography variant="body-xs">Buyer</Typography>

                                <DataGroup>
                                    <DataRowWithBadge>
                                        <DataContent>
                                            <DataRow>
                                                <Typography variant="body-sm" tag="span">
                                                    First Name:
                                                </Typography>
                                                {isFieldUpdated(currentDealData.buyer.firstName, newDealData.buyer.firstName) ||
                                                isFieldNew(currentDealData.buyer.firstName, newDealData.buyer.firstName) ? (
                                                    <Typography variant="strong-sm" tag="span">
                                                        {formatValue(newDealData.buyer.firstName)}
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="body-sm" tag="span">
                                                        {formatValue(newDealData.buyer.firstName)}
                                                    </Typography>
                                                )}
                                            </DataRow>
                                            {newDealData.buyer.middleName && (
                                                <DataRow>
                                                    <Typography variant="body-sm" tag="span">
                                                        Middle:
                                                    </Typography>
                                                    {isFieldUpdated(
                                                        currentDealData.buyer.middleName,
                                                        newDealData.buyer.middleName
                                                    ) ||
                                                    isFieldNew(currentDealData.buyer.middleName, newDealData.buyer.middleName) ? (
                                                        <Typography variant="strong-sm" tag="span">
                                                            {formatValue(newDealData.buyer.middleName)}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="body-sm" tag="span">
                                                            {formatValue(newDealData.buyer.middleName)}
                                                        </Typography>
                                                    )}
                                                </DataRow>
                                            )}
                                            <DataRow>
                                                <Typography variant="body-sm" tag="span">
                                                    Last Name:
                                                </Typography>
                                                {isFieldUpdated(currentDealData.buyer.lastName, newDealData.buyer.lastName) ||
                                                isFieldNew(currentDealData.buyer.lastName, newDealData.buyer.lastName) ? (
                                                    <Typography variant="strong-sm" tag="span">
                                                        {formatValue(newDealData.buyer.lastName)}
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="body-sm" tag="span">
                                                        {formatValue(newDealData.buyer.lastName)}
                                                    </Typography>
                                                )}
                                            </DataRow>
                                        </DataContent>
                                        {getGroupBadge(buyerNameHasUpdated, buyerNameHasNew)}
                                    </DataRowWithBadge>
                                </DataGroup>

                                <Typography variant="body-xs">Address</Typography>

                                <DataGroup>
                                    <DataRowWithBadge>
                                        <DataContent>
                                            <DataRow>
                                                <Typography variant="body-sm" tag="span">
                                                    Street:
                                                </Typography>
                                                {isFieldUpdated(
                                                    currentDealData.buyer.address.street,
                                                    newDealData.buyer.address.street
                                                ) ||
                                                isFieldNew(
                                                    currentDealData.buyer.address.street,
                                                    newDealData.buyer.address.street
                                                ) ? (
                                                    <Typography variant="strong-sm" tag="span">
                                                        {formatValue(newDealData.buyer.address.street)}
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="body-sm" tag="span">
                                                        {formatValue(newDealData.buyer.address.street)}
                                                    </Typography>
                                                )}
                                            </DataRow>
                                            <DataRow>
                                                <Typography variant="body-sm" tag="span">
                                                    City:
                                                </Typography>
                                                {isFieldUpdated(
                                                    currentDealData.buyer.address.city,
                                                    newDealData.buyer.address.city
                                                ) ||
                                                isFieldNew(currentDealData.buyer.address.city, newDealData.buyer.address.city) ? (
                                                    <Typography variant="strong-sm" tag="span">
                                                        {formatValue(newDealData.buyer.address.city)}
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="body-sm" tag="span">
                                                        {formatValue(newDealData.buyer.address.city)}
                                                    </Typography>
                                                )}
                                            </DataRow>
                                            <DataRow>
                                                <Typography variant="body-sm" tag="span">
                                                    State:
                                                </Typography>
                                                {isFieldUpdated(
                                                    currentDealData.buyer.address.state,
                                                    newDealData.buyer.address.state
                                                ) ||
                                                isFieldNew(currentDealData.buyer.address.state, newDealData.buyer.address.state) ? (
                                                    <Typography variant="strong-sm" tag="span">
                                                        {formatValue(newDealData.buyer.address.state)}
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="body-sm" tag="span">
                                                        {formatValue(newDealData.buyer.address.state)}
                                                    </Typography>
                                                )}
                                            </DataRow>
                                            <DataRow>
                                                <Typography variant="body-sm" tag="span">
                                                    ZIP:
                                                </Typography>
                                                {isFieldUpdated(currentDealData.buyer.address.zip, newDealData.buyer.address.zip) ||
                                                isFieldNew(currentDealData.buyer.address.zip, newDealData.buyer.address.zip) ? (
                                                    <Typography variant="strong-sm" tag="span">
                                                        {formatValue(newDealData.buyer.address.zip)}
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="body-sm" tag="span">
                                                        {formatValue(newDealData.buyer.address.zip)}
                                                    </Typography>
                                                )}
                                            </DataRow>
                                        </DataContent>
                                        {getGroupBadge(buyerAddressHasUpdated, buyerAddressHasNew)}
                                    </DataRowWithBadge>
                                </DataGroup>
                            </div>

                            {newDealData.coBuyer && (
                                <div>
                                    <Typography variant="body-xs">Co-Buyer</Typography>

                                    <DataGroup>
                                        <DataRowWithBadge>
                                            <DataContent>
                                                <DataRow>
                                                    <Typography variant="body-sm" tag="span">
                                                        First Name:
                                                    </Typography>
                                                    {!currentDealData.coBuyer ||
                                                    (currentDealData.coBuyer &&
                                                        (isFieldUpdated(
                                                            currentDealData.coBuyer.firstName,
                                                            newDealData.coBuyer.firstName
                                                        ) ||
                                                            isFieldNew(
                                                                currentDealData.coBuyer.firstName,
                                                                newDealData.coBuyer.firstName
                                                            ))) ? (
                                                        <Typography variant="strong-sm" tag="span">
                                                            {formatValue(newDealData.coBuyer.firstName)}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="body-sm" tag="span">
                                                            {formatValue(newDealData.coBuyer.firstName)}
                                                        </Typography>
                                                    )}
                                                </DataRow>
                                                {newDealData.coBuyer.middleName && (
                                                    <DataRow>
                                                        <Typography variant="body-sm" tag="span">
                                                            Middle:
                                                        </Typography>
                                                        {!currentDealData.coBuyer ||
                                                        (currentDealData.coBuyer &&
                                                            (isFieldUpdated(
                                                                currentDealData.coBuyer.middleName,
                                                                newDealData.coBuyer.middleName
                                                            ) ||
                                                                isFieldNew(
                                                                    currentDealData.coBuyer.middleName,
                                                                    newDealData.coBuyer.middleName
                                                                ))) ? (
                                                            <Typography variant="strong-sm" tag="span">
                                                                {formatValue(newDealData.coBuyer.middleName)}
                                                            </Typography>
                                                        ) : (
                                                            <Typography variant="body-sm" tag="span">
                                                                {formatValue(newDealData.coBuyer.middleName)}
                                                            </Typography>
                                                        )}
                                                    </DataRow>
                                                )}
                                                <DataRow>
                                                    <Typography variant="body-sm" tag="span">
                                                        Last Name:
                                                    </Typography>
                                                    {!currentDealData.coBuyer ||
                                                    (currentDealData.coBuyer &&
                                                        (isFieldUpdated(
                                                            currentDealData.coBuyer.lastName,
                                                            newDealData.coBuyer.lastName
                                                        ) ||
                                                            isFieldNew(
                                                                currentDealData.coBuyer.lastName,
                                                                newDealData.coBuyer.lastName
                                                            ))) ? (
                                                        <Typography variant="strong-sm" tag="span">
                                                            {formatValue(newDealData.coBuyer.lastName)}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="body-sm" tag="span">
                                                            {formatValue(newDealData.coBuyer.lastName)}
                                                        </Typography>
                                                    )}
                                                </DataRow>
                                            </DataContent>
                                            {getGroupBadge(!!coBuyerNameHasUpdated, !!coBuyerNameHasNew)}
                                        </DataRowWithBadge>
                                    </DataGroup>

                                    <Typography variant="body-xs">Address</Typography>

                                    <DataGroup>
                                        <DataRowWithBadge>
                                            <DataContent>
                                                <DataRow>
                                                    <Typography variant="body-sm" tag="span">
                                                        Street:
                                                    </Typography>
                                                    {!currentDealData.coBuyer ||
                                                    (currentDealData.coBuyer &&
                                                        (isFieldUpdated(
                                                            currentDealData.coBuyer.address.street,
                                                            newDealData.coBuyer.address.street
                                                        ) ||
                                                            isFieldNew(
                                                                currentDealData.coBuyer.address.street,
                                                                newDealData.coBuyer.address.street
                                                            ))) ? (
                                                        <Typography variant="strong-sm" tag="span">
                                                            {formatValue(newDealData.coBuyer.address.street)}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="body-sm" tag="span">
                                                            {formatValue(newDealData.coBuyer.address.street)}
                                                        </Typography>
                                                    )}
                                                </DataRow>
                                                <DataRow>
                                                    <Typography variant="body-sm" tag="span">
                                                        City:
                                                    </Typography>
                                                    {!currentDealData.coBuyer ||
                                                    (currentDealData.coBuyer &&
                                                        (isFieldUpdated(
                                                            currentDealData.coBuyer.address.city,
                                                            newDealData.coBuyer.address.city
                                                        ) ||
                                                            isFieldNew(
                                                                currentDealData.coBuyer.address.city,
                                                                newDealData.coBuyer.address.city
                                                            ))) ? (
                                                        <Typography variant="strong-sm" tag="span">
                                                            {formatValue(newDealData.coBuyer.address.city)}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="body-sm" tag="span">
                                                            {formatValue(newDealData.coBuyer.address.city)}
                                                        </Typography>
                                                    )}
                                                </DataRow>
                                                <DataRow>
                                                    <Typography variant="body-sm" tag="span">
                                                        State:
                                                    </Typography>
                                                    {!currentDealData.coBuyer ||
                                                    (currentDealData.coBuyer &&
                                                        (isFieldUpdated(
                                                            currentDealData.coBuyer.address.state,
                                                            newDealData.coBuyer.address.state
                                                        ) ||
                                                            isFieldNew(
                                                                currentDealData.coBuyer.address.state,
                                                                newDealData.coBuyer.address.state
                                                            ))) ? (
                                                        <Typography variant="strong-sm" tag="span">
                                                            {formatValue(newDealData.coBuyer.address.state)}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="body-sm" tag="span">
                                                            {formatValue(newDealData.coBuyer.address.state)}
                                                        </Typography>
                                                    )}
                                                </DataRow>
                                                <DataRow>
                                                    <Typography variant="body-sm" tag="span">
                                                        ZIP:
                                                    </Typography>
                                                    {!currentDealData.coBuyer ||
                                                    (currentDealData.coBuyer &&
                                                        (isFieldUpdated(
                                                            currentDealData.coBuyer.address.zip,
                                                            newDealData.coBuyer.address.zip
                                                        ) ||
                                                            isFieldNew(
                                                                currentDealData.coBuyer.address.zip,
                                                                newDealData.coBuyer.address.zip
                                                            ))) ? (
                                                        <Typography variant="strong-sm" tag="span">
                                                            {formatValue(newDealData.coBuyer.address.zip)}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="body-sm" tag="span">
                                                            {formatValue(newDealData.coBuyer.address.zip)}
                                                        </Typography>
                                                    )}
                                                </DataRow>
                                            </DataContent>
                                            {getGroupBadge(!!coBuyerAddressHasUpdated, !!coBuyerAddressHasNew)}
                                        </DataRowWithBadge>
                                    </DataGroup>
                                </div>
                            )}
                        </DataSection>
                    </ChangeSummaryColumn>
                </ChangeSummaryColumns>
            </ChangeSummarySection>
        </ReviewContainer>
    );
};
